import type { DocumentVersion, UserDocument, UserDocumentParticipantRole } from "@/api/models"

import {
  addDocumentParticipantApi,
  createDocumentApi,
  createDocumentFromMessageApi,
  type CreateDocumentPayload,
  deleteDocumentApi,
  getDocumentApi,
  listDocumentParticipantsApi,
  listDocumentsApi,
  listDocumentVersionsApi,
  removeDocumentParticipantApi,
  transferDocumentOwnershipApi,
  updateDocumentApi,
  updateDocumentParticipantRoleApi,
  type UpdateDocumentPayload,
} from "@/api/api"
import { logger } from "@/lib/logger"

export const DocumentService = {
  async listDocuments(projectId?: string, idToken?: string): Promise<UserDocument[]> {
    logger.debug("DocumentService.listDocuments", { projectId })
    return await listDocumentsApi(idToken, projectId)
  },

  async createDocument(payload: CreateDocumentPayload, idToken?: string): Promise<UserDocument> {
    logger.debug("DocumentService.createDocument", { title: payload.title })
    return await createDocumentApi(payload, idToken)
  },

  async createDocumentFromMessage(
    messageId: string,
    title?: string,
    idToken?: string
  ): Promise<UserDocument> {
    logger.debug("DocumentService.createDocumentFromMessage", { messageId })
    return await createDocumentFromMessageApi(messageId, title, idToken)
  },

  async getDocument(documentId: string, idToken?: string): Promise<UserDocument> {
    logger.debug("DocumentService.getDocument", { documentId })
    return await getDocumentApi(documentId, idToken)
  },

  async updateDocument(
    documentId: string,
    payload: UpdateDocumentPayload,
    idToken?: string
  ): Promise<UserDocument> {
    logger.debug("DocumentService.updateDocument", { documentId })
    return await updateDocumentApi(documentId, payload, idToken)
  },

  async deleteDocument(documentId: string, idToken?: string): Promise<{ status: string }> {
    logger.debug("DocumentService.deleteDocument", { documentId })
    return await deleteDocumentApi(documentId, idToken)
  },

  async listVersions(documentId: string, idToken?: string): Promise<DocumentVersion[]> {
    logger.debug("DocumentService.listVersions", { documentId })
    return await listDocumentVersionsApi(documentId, idToken)
  },

  async listParticipants(
    documentId: string,
    idToken?: string
  ): Promise<{
    participants: Array<{
      oid: string
      displayName: string
      type: "user" | "group"
      role: UserDocumentParticipantRole
    }>
    currentUserRole: UserDocumentParticipantRole
    ownerUserId: string
  }> {
    logger.debug("DocumentService.listParticipants", { documentId })
    return await listDocumentParticipantsApi(documentId, idToken)
  },

  async addParticipant(
    documentId: string,
    participant: {
      oid: string
      displayName: string
      type: "user" | "group"
      role: UserDocumentParticipantRole
    },
    idToken?: string
  ): Promise<UserDocument> {
    logger.debug("DocumentService.addParticipant", {
      documentId,
      participantOid: participant.oid,
    })
    return await addDocumentParticipantApi(documentId, participant, idToken)
  },

  async updateParticipantRole(
    documentId: string,
    participant: {
      oid: string
      role: UserDocumentParticipantRole
    },
    idToken?: string
  ): Promise<UserDocument> {
    logger.debug("DocumentService.updateParticipantRole", {
      documentId,
      participantOid: participant.oid,
      role: participant.role,
    })
    return await updateDocumentParticipantRoleApi(
      documentId,
      participant.oid,
      participant.role,
      idToken
    )
  },

  async removeParticipant(
    documentId: string,
    participantOid: string,
    idToken?: string
  ): Promise<UserDocument> {
    logger.debug("DocumentService.removeParticipant", { documentId, participantOid })
    return await removeDocumentParticipantApi(documentId, participantOid, idToken)
  },

  async transferOwnership(
    documentId: string,
    newOwnerOid: string,
    idToken?: string
  ): Promise<{ success: boolean; newOwnerOid: string; message: string }> {
    logger.debug("DocumentService.transferOwnership", { documentId, newOwnerOid })
    return await transferDocumentOwnershipApi(documentId, newOwnerOid, idToken)
  },
}
